import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Users, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { AvailabilityService } from '@/services/availabilityService';
import { AvailabilityPeriod } from '@/integrations/mysql/types';

interface AvailableDatesCardProps {
  propertyId: number;
  propertyMaxGuests?: number; // max_guests da propriedade como fallback
  isAuthenticated: boolean;
  onBookingClick: () => void;
}

const AvailableDatesCard = ({ propertyId, propertyMaxGuests, isAuthenticated, onBookingClick }: AvailableDatesCardProps) => {
  const [availablePeriods, setAvailablePeriods] = useState<AvailabilityPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(3);

  useEffect(() => {
    loadAvailablePeriods();
  }, [propertyId]);

  const loadAvailablePeriods = async () => {
    try {
      setLoading(true);
      const periods = await AvailabilityService.getAvailabilityPeriods(propertyId);
      
      // Filtrar apenas períodos futuros
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Filtrar períodos futuros usando datas exatas (sem timezone)
      const futurePeriods = periods.filter(period => {
        // Extrair apenas a parte da data (YYYY-MM-DD)
        const periodStartStr = typeof period.start_date === 'string' 
          ? period.start_date.substring(0, 10) 
          : `${period.start_date.getUTCFullYear()}-${String(period.start_date.getUTCMonth() + 1).padStart(2, '0')}-${String(period.start_date.getUTCDate()).padStart(2, '0')}`;
        
        // Comparar strings YYYY-MM-DD diretamente
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        return periodStartStr >= todayStr;
      });
      
      setAvailablePeriods(futurePeriods);
    } catch (error) {
      console.error('Erro ao carregar períodos disponíveis:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formatar data SEM timezone - usar exatamente como vem do banco (YYYY-MM-DD)
  const formatDate = (dateString: string) => {
    // Se já está no formato YYYY-MM-DD, extrair diretamente
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
      const monthIndex = parseInt(month, 10) - 1;
      return `${day} de ${monthNames[monthIndex]} de ${year}`;
    }
    
    // Fallback: se for outro formato, extrair apenas a parte da data (YYYY-MM-DD)
    const datePart = dateString.substring(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [year, month, day] = datePart.split('-');
      const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
      const monthIndex = parseInt(month, 10) - 1;
      return `${day} de ${monthNames[monthIndex]} de ${year}`;
    }
    
    return dateString;
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    // Extrair apenas a parte da data (YYYY-MM-DD) se vier como string ISO
    const startPart = startDate.substring(0, 10);
    const endPart = endDate.substring(0, 10);
    
    const formatDatePart = (datePart: string) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split('-');
        const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
        const monthIndex = parseInt(month, 10) - 1;
        return `${day} de ${monthNames[monthIndex]}`;
      }
      return datePart;
    };
    
    const formatDatePartWithYear = (datePart: string) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split('-');
        const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
        const monthIndex = parseInt(month, 10) - 1;
        return `${day} de ${monthNames[monthIndex]} de ${year}`;
      }
      return datePart;
    };
    
    const startFormatted = formatDatePart(startPart);
    const endFormatted = formatDatePartWithYear(endPart);
    
    return `${startFormatted} - ${endFormatted}`;
  };

  // Calcular noites SEM timezone - usar datas exatas do banco
  const calculateNights = (startDate: string, endDate: string) => {
    // Extrair apenas a parte da data (YYYY-MM-DD)
    const startPart = startDate.substring(0, 10);
    const endPart = endDate.substring(0, 10);
    
    // Parsear diretamente como YYYY-MM-DD usando UTC para evitar problemas de timezone
    const parseDate = (dateStr: string) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day));
      }
      return new Date(dateStr);
    };
    
    const start = parseDate(startPart);
    const end = parseDate(endPart);
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const totalPages = Math.ceil(availablePeriods.length / itemsPerPage);
  const currentPeriods = availablePeriods.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Datas Disponíveis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (availablePeriods.length === 0) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Datas Disponíveis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              Nenhuma data disponível no momento
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Datas Disponíveis ({availablePeriods.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {currentPeriods.map((period) => (
            <div
              key={period.id}
              className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">
                      {formatDateRange(period.start_date, period.end_date)}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {calculateNights(period.start_date, period.end_date)} noites
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>Até {period.max_guests && period.max_guests > 0 ? period.max_guests : (propertyMaxGuests || 0)} hóspedes</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4" />
                      <span className="font-medium text-primary">
                        R$ {period.price_per_night}/noite
                      </span>
                    </div>
                    {period.availability_type && (
                      <Badge variant="outline" className="text-xs">
                        {period.availability_type === 'troca' ? 'Troca' : 
                         period.availability_type === 'aluguel' ? 'Aluguel' : 
                         'Troca/Aluguel'}
                      </Badge>
                    )}
                  </div>
                  
                  {period.notes && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {period.notes}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              Página {currentPage + 1} de {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage === totalPages - 1}
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Botão de Reserva */}
        {isAuthenticated && availablePeriods.length > 0 && (
          <div className="mt-6 pt-4 border-t">
            <Button 
              onClick={onBookingClick}
              className="w-full"
              size="lg"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Ver Todas as Datas e Reservar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailableDatesCard;
